import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { workspaceApi } from '../api/apiClient';
import { useAuth } from '../context/AuthContext';
import { ArrowRightIcon, SearchIcon } from '../components/AppIcons';
import PersonalPrivacyLockIcon from '../components/PersonalPrivacyLockIcon';
import './WorkspaceDashboard.css';

function DocumentsIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7.5 3.75A2.75 2.75 0 0 0 4.75 6.5v11A2.75 2.75 0 0 0 7.5 20.25h9A2.75 2.75 0 0 0 19.25 17.5V9.23a2 2 0 0 0-.59-1.41l-3.48-3.48a2 2 0 0 0-1.41-.59H7.5Zm6 1.57 3.18 3.18H14a.5.5 0 0 1-.5-.5V5.32ZM8.75 12a.75.75 0 0 1 .75-.75h5a.75.75 0 0 1 0 1.5h-5a.75.75 0 0 1-.75-.75Zm0 3a.75.75 0 0 1 .75-.75h5a.75.75 0 0 1 0 1.5h-5a.75.75 0 0 1-.75-.75Z" />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5.75 4.75A2.75 2.75 0 0 0 3 7.5v7A2.75 2.75 0 0 0 5.75 17.25h2.7l2.37 2.37a1 1 0 0 0 1.68-.7v-1.67h5.75A2.75 2.75 0 0 0 21 14.5v-7A2.75 2.75 0 0 0 18.25 4.75h-12.5Z" />
    </svg>
  );
}

function TeamIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9 12.5A3.5 3.5 0 1 0 9 5.5a3.5 3.5 0 0 0 0 7Zm6 1a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM2.75 18.5A3.75 3.75 0 0 1 6.5 14.75h5A3.75 3.75 0 0 1 15.25 18.5v.75h-12.5v-.75Zm13 .75v-.5a5.2 5.2 0 0 0-1.1-3.2h2.1A3.25 3.25 0 0 1 20 18.8v.45h-4.25Z" />
    </svg>
  );
}

function AgendaIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 3.75A2.75 2.75 0 0 0 4.25 6.5v11A2.75 2.75 0 0 0 7 20.25h10A2.75 2.75 0 0 0 19.75 17.5v-11A2.75 2.75 0 0 0 17 3.75H7Zm0 1.5h10c.69 0 1.25.56 1.25 1.25v11c0 .69-.56 1.25-1.25 1.25H7c-.69 0-1.25-.56-1.25-1.25v-11c0-.69.56-1.25 1.25-1.25Zm1.5 3.25a.75.75 0 0 1 .75-.75h5.5a.75.75 0 1 1 0 1.5h-5.5a.75.75 0 0 1-.75-.75Zm0 4a.75.75 0 0 1 .75-.75h5.5a.75.75 0 1 1 0 1.5h-5.5a.75.75 0 0 1-.75-.75Zm0 4a.75.75 0 0 1 .75-.75h3.5a.75.75 0 1 1 0 1.5h-3.5a.75.75 0 0 1-.75-.75Z" />
    </svg>
  );
}

function RoleIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2.75 5 5.9v5.55c0 4.02 2.66 7.74 7 9.8 4.34-2.06 7-5.78 7-9.8V5.9l-7-3.15Zm0 2.05 5.5 2.47v4.18c0 3.14-2.03 6.11-5.5 7.93-3.47-1.82-5.5-4.79-5.5-7.93V7.27L12 4.8Zm0 2.7a2.75 2.75 0 0 0-2.75 2.75v.5h-1a.75.75 0 0 0-.75.75V15c0 .41.34.75.75.75h7.5a.75.75 0 0 0 .75-.75v-2.5a.75.75 0 0 0-.75-.75h-1v-.5A2.75 2.75 0 0 0 12 7.5Zm-1.25 2.75a1.25 1.25 0 1 1 2.5 0v.5h-2.5v-.5Z" />
    </svg>
  );
}

export default function WorkspaceDashboard() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [workspace, setWorkspace] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [removingUserId, setRemovingUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteCandidates, setInviteCandidates] = useState([]);
  const [inviteCandidatesLoading, setInviteCandidatesLoading] = useState(false);
  const [inviteRole, setInviteRole] = useState('VIEWER');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [accessError, setAccessError] = useState('');
  const [showEditDesc, setShowEditDesc] = useState(false);
  const [editDesc, setEditDesc] = useState('');
  const [editPersonalPrivate, setEditPersonalPrivate] = useState(false);
  const [editBackupEnabled, setEditBackupEnabled] = useState(true);
  const [editMaskingEnabled, setEditMaskingEnabled] = useState(false);
  const [editMaskingLiterals, setEditMaskingLiterals] = useState('');
  const [editMaskingRegexes, setEditMaskingRegexes] = useState('');
  const [backupExporting, setBackupExporting] = useState(false);
  const [editDescSaving, setEditDescSaving] = useState(false);
  const [editDescError, setEditDescError] = useState('');

  const loadWorkspace = useCallback(async () => {
    try {
      setAccessError('');
      const res = await workspaceApi.get(workspaceId);
      setWorkspace(res.data);
    } catch (err) {
      console.error('Failed to load workspace', err);
      if (err?.response?.status === 403 || err?.response?.status === 404) {
        setAccessError(err?.response?.data?.message || 'You do not have access to this workspace.');
      } else {
        setAccessError('Failed to load workspace.');
      }
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  useEffect(() => {
    const loadMembers = async () => {
      if (!workspaceId) return;
      setMembersLoading(true);
      try {
        const res = await workspaceApi.listMembers(workspaceId);
        setMembers(res.data || []);
      } catch (err) {
        console.error('Failed to load workspace members', err);
        setMembers([]);
      } finally {
        setMembersLoading(false);
      }
    };
    loadMembers();
  }, [workspaceId]);

  const canInviteMembers =
    workspace?.userRole === 'OWNER' ||
    workspace?.userRole === 'EDITOR' ||
    workspace?.userRole === 'ADMIN';

  const canEditWorkspace = canInviteMembers;

  const canRemoveMembers = workspace?.userRole === 'OWNER' || workspace?.userRole === 'ADMIN';

  const handleRemoveMember = async (member) => {
    if (!canRemoveMembers || !member?.userId) return;
    if (member.userId === user?.userId) return;
    if (member.role === 'OWNER') return;
    const ok = window.confirm(`Remove ${member.fullName || member.email} from this workspace?`);
    if (!ok) return;
    setRemovingUserId(member.userId);
    try {
      await workspaceApi.removeMember(workspaceId, member.userId);
      const res = await workspaceApi.listMembers(workspaceId);
      setMembers(res.data || []);
      await loadWorkspace();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Could not remove member.';
      window.alert(msg);
    } finally {
      setRemovingUserId(null);
    }
  };

  const openEditDescription = () => {
    if (!canEditWorkspace) return;
    setEditDesc(workspace.description ?? '');
    setEditPersonalPrivate(Boolean(workspace.personalPrivate));
    setEditBackupEnabled(workspace.backupEnabled !== false);
    setEditMaskingEnabled(Boolean(workspace.maskingEnabled));
    setEditMaskingLiterals((workspace.maskingLiterals || []).join('\n'));
    setEditMaskingRegexes((workspace.maskingRegexes || []).join('\n'));
    setEditDescError('');
    setShowEditDesc(true);
  };

  const handleSaveDescription = async (e) => {
    e.preventDefault();
    if (!workspace) return;
    setEditDescError('');
    setEditDescSaving(true);
    try {
      const res = await workspaceApi.update(workspaceId, {
        name: workspace.name,
        description: editDesc,
        color: workspace.color,
        personalPrivate: editPersonalPrivate,
        backupEnabled: editBackupEnabled,
        maskingEnabled: editMaskingEnabled,
        maskingLiterals: editMaskingLiterals
          .split('\n')
          .map((x) => x.trim())
          .filter(Boolean),
        maskingRegexes: editMaskingRegexes
          .split('\n')
          .map((x) => x.trim())
          .filter(Boolean),
      });
      setWorkspace(res.data);
      setShowEditDesc(false);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Could not save description.';
      setEditDescError(msg);
    } finally {
      setEditDescSaving(false);
    }
  };

  const handleExportBackup = async () => {
    setBackupExporting(true);
    try {
      const res = await workspaceApi.exportBackup(workspaceId);
      const blob = new Blob([res.data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${workspace?.name || 'workspace'}-backup.json`;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      window.alert(err?.response?.data?.message || 'Failed to export backup.');
    } finally {
      setBackupExporting(false);
    }
  };

  const openInviteModal = async () => {
    if (!canInviteMembers) return;
    setInviteEmail('');
    setInviteCandidates([]);
    setInviteRole('VIEWER');
    setInviteError('');
    setInviteSuccess('');
    setShowInvite(true);
    setInviteCandidatesLoading(true);
    try {
      const res = await workspaceApi.listInviteCandidates(workspaceId);
      const candidates = Array.isArray(res.data) ? res.data : [];
      setInviteCandidates(candidates);
      setInviteEmail(candidates[0]?.email || '');
    } catch (err) {
      setInviteCandidates([]);
      setInviteError(err?.response?.data?.message || 'Could not load available users.');
    } finally {
      setInviteCandidatesLoading(false);
    }
  };

  const handleInviteMember = async (e) => {
    e.preventDefault();
    setInviteError('');
    setInviteSuccess('');
    setInviteLoading(true);
    try {
      await workspaceApi.inviteMember(workspaceId, inviteEmail.trim().toLowerCase(), inviteRole);
      setInviteSuccess('Invitation sent successfully.');
      await loadWorkspace();
      try {
        const [memberRes, candidateRes] = await Promise.all([
          workspaceApi.listMembers(workspaceId),
          workspaceApi.listInviteCandidates(workspaceId),
        ]);
        setMembers(memberRes.data || []);
        const candidates = Array.isArray(candidateRes.data) ? candidateRes.data : [];
        setInviteCandidates(candidates);
        setInviteEmail(candidates[0]?.email || '');
      } catch {
        /* ignore */
      }
    } catch (err) {
      const serverMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        '';

      if (serverMessage.toLowerCase().includes('not registered')) {
        setInviteError('This email is not a Ventus user yet. Ask them to create an account first.');
      } else if (serverMessage.toLowerCase().includes('not a member')) {
        setInviteError('You cannot invite users because you are not a member of this workspace.');
      } else if (serverMessage.toLowerCase().includes('permission')) {
        setInviteError('You do not have permission to invite members in this workspace.');
      } else {
        setInviteError(serverMessage || 'Failed to invite member. Check the email and try again.');
      }
    } finally {
      setInviteLoading(false);
    }
  };

  if (loading) return <div className="v-loading">Loading workspace...</div>;
  if (!workspace) {
    return (
      <div className="v-page">
        <div className="v-empty">
          <h3>Workspace unavailable</h3>
          <p>{accessError || 'Workspace not found'}</p>
          <button className="v-btn v-btn--primary" onClick={() => navigate('/workspaces')}>
            Back to workspaces
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="v-page">
      {/* Breadcrumb */}
      <div className="v-breadcrumb">
        <button onClick={() => navigate('/workspaces')}>Workspaces</button>
        <span className="v-breadcrumb__sep">›</span>
        <span>{workspace.name}</span>
      </div>

      <div className="v-page__header">
        <div>
          <div className="v-ws-title-row">
            <div className="v-ws-icon" style={{ background: workspace.color || '#DC2626' }}>
              {workspace.name?.charAt(0)?.toUpperCase()}
            </div>
            <h1 className="v-page__title">{workspace.name}</h1>
          </div>
          <div className="v-ws-desc-row">
            <p className="v-page__subtitle v-ws-desc-row__text">
              {workspace.description?.trim()
                ? workspace.description
                : canEditWorkspace
                  ? 'No description yet — add one so your team knows what this space is for.'
                  : 'No description.'}
            </p>
            {canEditWorkspace && (
              <button
                type="button"
                className="v-btn v-btn--ghost v-btn--sm v-ws-desc-row__edit"
                onClick={openEditDescription}
              >
                Workspace settings
              </button>
            )}
          </div>
          {workspace.personalPrivate && (
            <p className="v-page__subtitle v-page__subtitle--privacy-row" style={{ marginTop: 6 }}>
              <PersonalPrivacyLockIcon size="sm" title="Personal-private workspace" />
              <span>Personal-private: hidden from platform admins unless they are members.</span>
            </p>
          )}
          {workspace.maskingEnabled && (
            <p className="v-page__subtitle" style={{ marginTop: 6 }}>
              🛡️ AI masking enabled: configured literals and regex patterns are masked before AI requests.
            </p>
          )}
        </div>
        <div className="v-page__actions">
          <button
            type="button"
            className="v-btn v-btn--secondary"
            onClick={handleExportBackup}
            disabled={backupExporting || workspace.backupEnabled === false}
          >
            {backupExporting ? 'Exporting backup…' : 'Export backup'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="v-stats-row">
        <div className="v-stat-card">
          <span className="v-stat-card__icon"><DocumentsIcon /></span>
          <div className="v-stat-card__meta">
            <span className="v-stat-card__value">{workspace.documentCount}</span>
            <span className="v-stat-card__label">Documents</span>
          </div>
        </div>
        <div className="v-stat-card">
          <span className="v-stat-card__icon"><TeamIcon /></span>
          <div className="v-stat-card__meta">
            <span className="v-stat-card__value">{workspace.memberCount}</span>
            <span className="v-stat-card__label">Members</span>
          </div>
        </div>
        <div className="v-stat-card">
          <span className="v-stat-card__icon"><RoleIcon /></span>
          <div className="v-stat-card__meta">
            <span className="v-stat-card__value">{workspace.userRole}</span>
            <span className="v-stat-card__label">Your role</span>
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="v-actions-grid">
        <div
          className="v-action-card"
          onClick={() => navigate(`/workspaces/${workspaceId}/documents`)}
        >
          <span className="v-action-card__icon"><DocumentsIcon /></span>
          <h3>Knowledge Documents</h3>
          <p>Upload, review, and manage source files that feed your knowledge base.</p>
          <span className="v-action-card__cta">
            Open uploads
            <span className="v-action-card__cta-icon"><ArrowRightIcon /></span>
          </span>
        </div>

        <div
          className="v-action-card"
          onClick={() => navigate(`/workspaces/${workspaceId}/search`)}
        >
          <span className="v-action-card__icon"><SearchIcon /></span>
          <h3>Search documents</h3>
          <p>Keyword search across file names and indexed chunks (no AI).</p>
          <span className="v-action-card__cta">
            Open search
            <span className="v-action-card__cta-icon"><ArrowRightIcon /></span>
          </span>
        </div>

        <div
          className="v-action-card"
          onClick={() => navigate(`/workspaces/${workspaceId}/qa`)}
        >
          <span className="v-action-card__icon"><ChatIcon /></span>
          <h3>Document Chat</h3>
          <p>Ask questions against uploaded files and receive cited, auditable answers.</p>
          <span className="v-action-card__cta">
            Open chat
            <span className="v-action-card__cta-icon"><ArrowRightIcon /></span>
          </span>
        </div>

        <div
          className="v-action-card"
          onClick={() => navigate(`/workspaces/${workspaceId}/agenda`)}
        >
          <span className="v-action-card__icon"><AgendaIcon /></span>
          <h3>Agenda & reminders</h3>
          <p>See all agenda items from Q&A in one place and track what is due soon.</p>
          <span className="v-action-card__cta">
            Open agenda
            <span className="v-action-card__cta-icon"><ArrowRightIcon /></span>
          </span>
        </div>

        <div className="v-action-card v-action-card--muted">
          <span className="v-action-card__icon"><TeamIcon /></span>
          <h3>Team Members</h3>
          <p>People with access to this workspace. Owners and editors can invite others.</p>
          {membersLoading ? (
            <p className="v-member-list__loading">Loading members…</p>
          ) : members.length === 0 ? (
            <p className="v-member-list__empty">No members loaded.</p>
          ) : (
            <ul className="v-member-list">
              {members.map((m) => (
                <li key={m.userId} className="v-member-list__row">
                  <div className="v-member-list__avatar" aria-hidden>
                    {(m.fullName || m.email || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="v-member-list__meta">
                    <span className="v-member-list__name">{m.fullName || 'Unknown'}</span>
                    <span className="v-member-list__email">{m.email}</span>
                  </div>
                  <span className={`v-member-list__role v-member-list__role--${(m.role || 'VIEWER').toLowerCase()}`}>
                    {m.role}
                  </span>
                  {canRemoveMembers &&
                    m.userId !== user?.userId &&
                    m.role !== 'OWNER' &&
                    workspace?.ownerId !== m.userId && (
                      <button
                        type="button"
                        className="v-btn v-btn--ghost v-btn--sm v-member-list__remove"
                        disabled={removingUserId === m.userId}
                        onClick={() => handleRemoveMember(m)}
                      >
                        {removingUserId === m.userId ? '…' : 'Remove'}
                      </button>
                    )}
                </li>
              ))}
            </ul>
          )}
          <div className="v-action-card__footer">
            <span className="v-action-card__cta v-action-card__cta--neutral">
              {workspace.memberCount} member{workspace.memberCount !== 1 ? 's' : ''}
            </span>
            {canInviteMembers && (
              <button
                type="button"
                className="v-btn v-btn--primary v-btn--sm v-invite-btn"
                onClick={openInviteModal}
              >
                Invite member
              </button>
            )}
          </div>
        </div>
      </div>

      {showEditDesc && (
        <div className="v-modal-backdrop" onClick={() => !editDescSaving && setShowEditDesc(false)}>
          <div className="v-modal v-settings-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="v-settings-modal__title">Workspace settings</h3>
            <p className="v-settings-modal__subtitle">
              Update description, privacy mode, backup export, and masking rules in one place.
            </p>
            <form onSubmit={handleSaveDescription}>
              <div className="v-settings-modal__section">
                <h4 className="v-settings-modal__section-title">Workspace info</h4>
                <div className="v-form-group">
                  <label htmlFor="ws-edit-desc">Description</label>
                  <textarea
                    id="ws-edit-desc"
                    value={editDesc}
                    onChange={(e) => setEditDesc(e.target.value)}
                    placeholder="What is this workspace for?"
                    rows={3}
                    autoFocus
                  />
                </div>
              </div>

              <div className="v-settings-modal__section">
                <h4 className="v-settings-modal__section-title">Security and data controls</h4>
                <div className="v-settings-toggle-grid">
                  <label className="v-settings-toggle-card">
                    <input
                      type="checkbox"
                      checked={editPersonalPrivate}
                      onChange={(e) => setEditPersonalPrivate(e.target.checked)}
                    />
                    <span className="v-settings-toggle-card__title-row">
                      <PersonalPrivacyLockIcon size="sm" title="Higher privacy workspace" />
                      <span>Higher privacy (personal)</span>
                    </span>
                    <span className="v-settings-toggle-card__desc">
                      Hide this workspace from platform admins unless they are members.
                    </span>
                  </label>

                  <label className="v-settings-toggle-card">
                    <input
                      type="checkbox"
                      checked={editBackupEnabled}
                      onChange={(e) => setEditBackupEnabled(e.target.checked)}
                    />
                    <span className="v-settings-toggle-card__title">Allow backup exports</span>
                    <span className="v-settings-toggle-card__desc">
                      Enable users with edit rights to export a workspace backup JSON.
                    </span>
                  </label>

                  <label className="v-settings-toggle-card">
                    <input
                      type="checkbox"
                      checked={editMaskingEnabled}
                      onChange={(e) => setEditMaskingEnabled(e.target.checked)}
                    />
                    <span className="v-settings-toggle-card__title">Mask sensitive data before AI</span>
                    <span className="v-settings-toggle-card__desc">
                      Redact configured literals and regex matches before Q&A context is sent to AI.
                    </span>
                  </label>
                </div>
              </div>

              <div className="v-settings-modal__section">
                <h4 className="v-settings-modal__section-title">Masking rules</h4>
                <p className="v-settings-modal__hint">
                  Add one rule per line. <strong>Masking literals</strong> and <strong>Masking regex patterns</strong> are
                  independent lists; both are applied before sending content to AI. Keep patterns precise to avoid
                  unnecessary masking and weaker AI answers.
                </p>
                <div className="v-settings-rules-grid">
                  <div className="v-form-group">
                    <label htmlFor="ws-mask-literals">Masking literals</label>
                    <textarea
                      id="ws-mask-literals"
                      className="v-settings-modal__textarea"
                      value={editMaskingLiterals}
                      onChange={(e) => setEditMaskingLiterals(e.target.value)}
                      rows={6}
                      placeholder={'One per line\nAcme BV\nJohn Doe\n123 Main Street'}
                    />
                  </div>
                  <div className="v-form-group">
                    <label htmlFor="ws-mask-regex">Masking regex patterns</label>
                    <textarea
                      id="ws-mask-regex"
                      className="v-settings-modal__textarea v-settings-modal__textarea--code"
                      value={editMaskingRegexes}
                      onChange={(e) => setEditMaskingRegexes(e.target.value)}
                      rows={6}
                      placeholder={'One per line\n\\b\\d{3}-\\d{2}-\\d{4}\\b\n\\b[A-Z]{2}\\d{8}\\b'}
                    />
                  </div>
                </div>
              </div>
              {editDescError && <div className="v-invite-message v-invite-message--error">{editDescError}</div>}
              <div className="v-modal__actions">
                <button
                  type="button"
                  className="v-btn v-btn--ghost"
                  onClick={() => setShowEditDesc(false)}
                  disabled={editDescSaving}
                >
                  Cancel
                </button>
                <button type="submit" className="v-btn v-btn--primary" disabled={editDescSaving}>
                  {editDescSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showInvite && (
        <div className="v-modal-backdrop" onClick={() => setShowInvite(false)}>
          <div className="v-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Invite member</h3>
            <form onSubmit={handleInviteMember}>
              <div className="v-form-group">
                <label>Available users</label>
                <select
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  required
                  autoFocus
                  disabled={inviteCandidatesLoading || inviteLoading || inviteCandidates.length === 0}
                >
                  {inviteCandidatesLoading ? (
                    <option value="">Loading users...</option>
                  ) : inviteCandidates.length === 0 ? (
                    <option value="">No users available to invite</option>
                  ) : (
                    inviteCandidates.map((candidate) => (
                      <option key={candidate.userId || candidate.email} value={candidate.email}>
                        {candidate.fullName ? `${candidate.fullName} (${candidate.email})` : candidate.email}
                      </option>
                    ))
                  )}
                </select>
              </div>

              <div className="v-form-group">
                <label>Role</label>
                <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                  <option value="VIEWER">Viewer</option>
                  <option value="EDITOR">Editor</option>
                </select>
              </div>

              {inviteError && <div className="v-invite-message v-invite-message--error">{inviteError}</div>}
              {inviteSuccess && <div className="v-invite-message v-invite-message--success">{inviteSuccess}</div>}

              <div className="v-modal__actions">
                <button type="button" className="v-btn v-btn--ghost" onClick={() => setShowInvite(false)}>
                  Close
                </button>
                <button
                  type="submit"
                  className="v-btn v-btn--primary"
                  disabled={inviteLoading || inviteCandidatesLoading || !inviteEmail}
                >
                  {inviteLoading ? 'Inviting...' : 'Send invite'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
